import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAnnouncement,
  createAnnouncement,
  updateAnnouncement,
} from "@/services/firebase/announcement";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";
import { AdminPage, AdminPageHeader, AdminLoadingSkeleton, AdminErrorAlert } from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const announcementSchema = z
  .object({
    title: z.string().min(1, "標題為必填"),
    content: z.string().min(1, "內容為必填"),
    isActive: z.boolean(),
    imageURL: z.string().optional(),
    ctaText: z.string().optional(),
    ctaLink: z
      .string()
      .optional()
      .refine(
        (val) => !val || val === "" || z.string().url().safeParse(val).success,
        { message: "請輸入有效的 URL" }
      ),
    displayMode: z.enum(["once", "everyTime", "daily"]),
    targetAudience: z.array(z.string()).optional(),
    startAt: z.string().optional(),
    endAt: z.string().optional(),
  })
  .refine(
    (data) => {
      // 如果提供了結束時間，必須提供開始時間
      if (data.endAt && !data.startAt) {
        return false;
      }
      // 如果提供了開始和結束時間，結束時間必須晚於開始時間
      if (data.startAt && data.endAt) {
        return new Date(data.endAt) > new Date(data.startAt);
      }
      return true;
    },
    {
      message: "結束時間必須晚於開始時間",
      path: ["endAt"],
    }
  );

type AnnouncementFormData = z.infer<typeof announcementSchema>;

export default function AnnouncementEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");

  // 載入公告數據
  const { data: announcement, isLoading } = useQuery({
    queryKey: ["announcement", actualId],
    queryFn: () => getAnnouncement(actualId!),
    enabled: !isNew && !!actualId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<AnnouncementFormData>({
    resolver: zodResolver(announcementSchema),
    defaultValues: {
      title: "",
      content: "",
      isActive: true,
      imageURL: "",
      ctaText: "",
      ctaLink: "",
      displayMode: "once",
      targetAudience: [],
      startAt: "",
      endAt: "",
    },
  });

  const imageURL = watch("imageURL");
  const isActive = watch("isActive");
  const displayMode = watch("displayMode");

  // 初始化表單數據
  useEffect(() => {
    if (announcement && !isNew) {
      setValue("title", announcement.title || "");
      setValue("content", announcement.content || "");
      setValue("isActive", announcement.isActive ?? true);
      setValue("imageURL", announcement.imageURL || "");
      setValue("ctaText", announcement.ctaText || "");
      setValue("ctaLink", announcement.ctaLink || "");
      setValue("displayMode", announcement.displayMode || "once");
      setValue("targetAudience", announcement.targetAudience || []);
      setValue(
        "startAt",
        announcement.startAt
          ? new Date(announcement.startAt).toISOString().slice(0, 16)
          : ""
      );
      setValue(
        "endAt",
        announcement.endAt
          ? new Date(announcement.endAt).toISOString().slice(0, 16)
          : ""
      );
    }
  }, [announcement, setValue, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: AnnouncementFormData) => {
      const announcementData = {
        title: data.title,
        content: data.content,
        isActive: data.isActive,
        displayMode: data.displayMode,
        imageURL: data.imageURL || undefined,
        ctaText: data.ctaText || undefined,
        ctaLink: data.ctaLink || undefined,
        targetAudience: data.targetAudience && data.targetAudience.length > 0
          ? data.targetAudience
          : undefined,
        startAt: data.startAt ? new Date(data.startAt) : undefined,
        endAt: data.endAt ? new Date(data.endAt) : undefined,
      };

      if (isNew) {
        // 新增模式
        return await createAnnouncement(announcementData);
      } else {
        // 編輯模式
        if (!actualId) {
          throw new Error("缺少公告 ID");
        }
        await updateAnnouncement(actualId, announcementData);
        return actualId;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      queryClient.invalidateQueries({ queryKey: ["announcement", savedId] });
      setSaveError(null);
      navigate("/announcements");
    },
    onError: (error) => {
      console.error("儲存公告失敗:", error);
      setSaveError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const onSubmit = (data: AnnouncementFormData) => {
    mutation.mutate(data);
  };

  const handleTargetAudienceChange = (value: string, checked: boolean) => {
    const current = watch("targetAudience") || [];
    if (checked) {
      setValue("targetAudience", [...current, value]);
    } else {
      setValue("targetAudience", current.filter((v) => v !== value));
    }
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增公告" : "編輯公告"}
        actions={
          <Button variant="outline" onClick={() => navigate("/announcements")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 錯誤訊息 */}
        {saveError && (
          <AdminErrorAlert
            message={`儲存失敗：${saveError}`}
            onDismiss={() => setSaveError(null)}
          />
        )}

        {/* 基本資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                標題 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                {...register("title")}
                placeholder="輸入公告標題"
                className={errors.title ? "border-destructive" : ""}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">
                內容 <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="content"
                {...register("content")}
                rows={6}
                placeholder="輸入公告內容（可含文字、HTML 或 Markdown）"
                className={errors.content ? "border-destructive" : ""}
              />
              {errors.content && (
                <p className="text-sm text-destructive">{errors.content.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setValue("isActive", checked)}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                啟用公告
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* 圖片上傳 */}
        <Card>
          <CardHeader>
            <CardTitle>圖片</CardTitle>
          </CardHeader>
          <CardContent>
            <ImageUpload
              value={imageURL}
              onChange={(url) => setValue("imageURL", url, { shouldDirty: true, shouldValidate: true })}
              storagePath="announcements_images"
              label="公告圖片"
              maxSizeMB={5}
            />
          </CardContent>
        </Card>

        {/* CTA 按鈕 */}
        <Card>
          <CardHeader>
            <CardTitle>Call to Action 按鈕</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ctaText">按鈕文字</Label>
              <Input
                id="ctaText"
                {...register("ctaText")}
                placeholder="例如：立即查看"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ctaLink">按鈕連結</Label>
              <Input
                id="ctaLink"
                type="url"
                {...register("ctaLink")}
                placeholder="https://example.com"
                className={errors.ctaLink ? "border-destructive" : ""}
              />
              {errors.ctaLink && (
                <p className="text-sm text-destructive">{errors.ctaLink.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 顯示設定 */}
        <Card>
          <CardHeader>
            <CardTitle>顯示設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                顯示模式 <span className="text-destructive">*</span>
              </Label>
              <Select
                value={displayMode}
                onValueChange={(value) =>
                  setValue("displayMode", value as "once" | "everyTime" | "daily")
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="選擇顯示模式" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">一次性</SelectItem>
                  <SelectItem value="everyTime">每次顯示</SelectItem>
                  <SelectItem value="daily">每日顯示</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>目標受眾</Label>
              <div className="space-y-2">
                {["all", "twUsers", "myUsers"].map((audience) => (
                  <label key={audience} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(watch("targetAudience") || []).includes(audience)}
                      onChange={(e) =>
                        handleTargetAudienceChange(audience, e.target.checked)
                      }
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    <span className="text-sm text-foreground">
                      {audience === "all"
                        ? "所有用戶"
                        : audience === "twUsers"
                        ? "台灣用戶"
                        : "馬來西亞用戶"}
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                若不選擇，則對所有用戶顯示
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="startAt">開始時間</Label>
              <Input
                id="startAt"
                type="datetime-local"
                {...register("startAt")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endAt">結束時間</Label>
              <Input
                id="endAt"
                type="datetime-local"
                {...register("endAt")}
                className={errors.endAt ? "border-destructive" : ""}
              />
              {errors.endAt && (
                <p className="text-sm text-destructive">{errors.endAt.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/announcements")}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {mutation.isPending ? "儲存中..." : "儲存"}
          </Button>
        </div>
      </form>
    </AdminPage>
  );
}
